// API Client with error handling
import { state } from './state.js';

export class APIClient {
    constructor(baseURL = '/api') {
        this.baseURL = baseURL;
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        if (state.authToken) {
            headers['Authorization'] = `Bearer ${state.authToken}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers
            });

            const text = await response.text();

            if (!response.ok) {
                let msg = `HTTP ${response.status}`;
                if (text) {
                    try {
                        const errObj = JSON.parse(text);
                        if (errObj && errObj.error) msg = errObj.error;
                    } catch (_) {
                        msg = text.length > 200 ? text.slice(0, 200) + '…' : text;
                    }
                }
                throw new Error(msg);
            }

            const trimmed = (text || '').trim();
            if (!trimmed) {
                return {};
            }
            try {
                return JSON.parse(trimmed);
            } catch (e) {
                throw new Error(
                    `Invalid JSON from ${endpoint}: ${e instanceof Error ? e.message : String(e)}`
                );
            }
        } catch (error) {
            console.error(`[API] ${options.method || 'GET'} ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Generic HTTP methods
    async get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }

    async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }

    // Threats / Signatures
    async getThreats() {
        return this.request('/threats');
    }

    async getSignatures() {
        return this.request('/signatures');
    }

    // Settings
    async getSettings() {
        return this.request('/settings');
    }

    async updateSettings(settings) {
        return this.request('/settings', {
            method: 'PUT',
            body: JSON.stringify(settings)
        });
    }

    // File Uploads
    async uploadLogo(file) {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${this.baseURL}/upload/logo`;
        const headers = {};

        if (state.authToken) {
            headers['Authorization'] = `Bearer ${state.authToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData
        });

        const text = await response.text();

        if (!response.ok) {
            let msg = `HTTP ${response.status}`;
            if (text) {
                try {
                    const errObj = JSON.parse(text);
                    if (errObj && errObj.error) msg = errObj.error;
                } catch (_) {
                    msg = text.length > 200 ? text.slice(0, 200) + '…' : text;
                }
            }
            throw new Error(msg);
        }

        const trimmed = (text || '').trim();
        if (!trimmed) return {};
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            throw new Error(`Invalid JSON from /upload/logo: ${e instanceof Error ? e.message : String(e)}`);
        }
    }

    async getCurrentLogo() {
        return this.request('/logo');
    }

    async deleteFile(category, filename) {
        return this.request(`/files/${category}/${filename}`, {
            method: 'DELETE'
        });
    }

    // Password Policy
    async getPasswordPolicy() {
        return this.request('/password-policy');
    }

    async updatePasswordPolicy(policy) {
        return this.request('/password-policy', {
            method: 'PUT',
            body: JSON.stringify(policy)
        });
    }

    // Users
    async getUsers() {
        return this.request('/users');
    }

    async createUser(user) {
        return this.request('/users', {
            method: 'POST',
            body: JSON.stringify(user)
        });
    }

    async updateUser(id, data) {
        return this.request(`/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async deleteUser(id) {
        return this.request(`/users/${id}`, {
            method: 'DELETE'
        });
    }

    // MFA
    async getMFAStatus() {
        return this.request('/mfa/status');
    }

    async setupMFA() {
        return this.request('/mfa/setup', { method: 'POST', body: '{}' });
    }

    async enableMFA(code) {
        return this.request('/mfa/enable', {
            method: 'POST',
            body: JSON.stringify({ code })
        });
    }

    async disableMFA(code, password) {
        return this.request('/mfa/disable', {
            method: 'POST',
            body: JSON.stringify({ code, password })
        });
    }

    // Roles
    async getRoles() {
        return this.request('/roles');
    }

    async createRole(role) {
        return this.request('/roles', {
            method: 'POST',
            body: JSON.stringify(role)
        });
    }

    async updateRole(id, role) {
        return this.request(`/roles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(role)
        });
    }

    async deleteRole(id) {
        return this.request(`/roles/${id}`, {
            method: 'DELETE'
        });
    }

    // Licenses
    async getLicenses() {
        return this.request('/licenses');
    }

    async createLicense(license) {
        return this.request('/licenses', {
            method: 'POST',
            body: JSON.stringify(license)
        });
    }

    async revokeLicense(id) {
        return this.request(`/licenses/${id}`, {
            method: 'DELETE'
        });
    }

    // Packages
    async getPackages() {
        return this.request('/packages');
    }

    async createPackage(pkg) {
        return this.request('/packages', {
            method: 'POST',
            body: JSON.stringify(pkg)
        });
    }

    async updatePackage(id, pkg) {
        return this.request(`/packages/${id}`, {
            method: 'PUT',
            body: JSON.stringify(pkg)
        });
    }

    async deletePackage(id) {
        return this.request(`/packages/${id}`, {
            method: 'DELETE'
        });
    }

    // Clients
    async getClients() {
        return this.request('/clients');
    }

    async getClient(id) {
        return this.request(`/clients/${id}`);
    }

    async createClient(client) {
        return this.request('/clients', {
            method: 'POST',
            body: JSON.stringify(client)
        });
    }

    async updateClient(id, client) {
        return this.request(`/clients/${id}`, {
            method: 'PUT',
            body: JSON.stringify(client)
        });
    }

    async deleteClient(id) {
        return this.request(`/clients/${id}`, {
            method: 'DELETE'
        });
    }

    // Stats
    async getStats() {
        return this.request('/stats');
    }

    // Agents
    async getAgents() {
        return this.request('/agents');
    }

    async getAgent(id) {
        return this.request(`/agents/${encodeURIComponent(id)}`);
    }

    async triggerScan(agentId, path = '', type = 'quick') {
        return this.request(`/agents/${agentId}/scan`, {
            method: 'POST',
            body: JSON.stringify({ path, type })
        });
    }

    async deleteAgent(id) {
        return this.request(`/agents/${encodeURIComponent(id)}`, {
            method: 'DELETE'
        });
    }

    // Invoices
    async getInvoices() {
        return this.request('/invoices');
    }

    async getInvoice(id) {
        return this.request(`/invoices/${id}`);
    }

    async createInvoice(invoice) {
        return this.request('/invoices', {
            method: 'POST',
            body: JSON.stringify(invoice)
        });
    }

    async updateInvoice(id, invoice) {
        return this.request(`/invoices/${id}`, {
            method: 'PUT',
            body: JSON.stringify(invoice)
        });
    }

    async deleteInvoice(id) {
        return this.request(`/invoices/${id}`, {
            method: 'DELETE'
        });
    }

    async downloadInvoicePDF(id) {
        const url = `${this.baseURL}/invoices/${id}/pdf`;
        const headers = {};
        if (state.authToken) {
            headers['Authorization'] = `Bearer ${state.authToken}`;
        }
        const response = await fetch(url, { headers });
        if (!response.ok) {
            let msg = `HTTP ${response.status}`;
            try {
                const err = await response.json();
                if (err && err.error) msg = err.error;
            } catch (_) { /* ignore */ }
            throw new Error(msg);
        }
        return response.blob();
    }
}

/**
 * Parse fetch Response as JSON without throwing SyntaxError on empty/invalid body
 * (avoids "Unexpected end of input" surfaced as agents:LINE in the global error handler).
 */
export async function safeReadJson(response, defaultIfEmpty = null) {
    const text = await response.text();
    const trimmed = (text || '').trim();
    if (!trimmed) {
        return defaultIfEmpty;
    }
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(`Invalid JSON response: ${msg}`);
    }
}

export const api = new APIClient();
